import ConfirmationModal from '@components/ConfirmationModal'
import { ErrorNote } from '@components/Notice'
import { PageScroll } from '@components/Page'
import PageLoader from '@components/PageLoader'
import { useEvents, useFiles, useTrip, useTripDayInfos } from '@hooks'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { eventName } from '../EventCreateFlow/core/draft'
import { EventCreateStep } from '../EventCreateFlow/core/presentation'
import EventFlow from '../EventCreateFlow/EventFlow'
import { itineraryPath } from '../QuickEdit/paths'
import { editBottomBar } from './core/editPresentation'
import { EventEditState, hasChanges, removedFileIds, seedEditState, toUpdatePayload, updateDraft } from './core/editState'
import { eventIdFromParam } from './paths'
import { useSaveEvent } from './useSaveEvent'

/**
 * The edit counterpart of the create-event flow, as Android has it: the same
 * What, When and Extras pages over a saved event, with Save sending only what
 * changed, then the attachment changes. Opened from the event's details on
 * the itinerary; Save returns there on the event's day, and closing asks
 * before discarding edits.
 */
const EventEditPage: React.FC = () => {
	const { tripId = '', eventId: eventIdParam } = useParams<{ tripId: string; eventId: string }>()
	const eventId = eventIdFromParam(eventIdParam)
	const navigate = useNavigate()

	const { data: trip, isLoading: tripLoading, error: tripError } = useTrip(tripId)
	const { data: eventsData, isLoading: eventsLoading, error: eventsError } = useEvents(tripId)
	const { data: filesData, isLoading: filesLoading, error: filesError } = useFiles(tripId)
	const { data: dayInfosData } = useTripDayInfos(tripId)
	const { saveEvent } = useSaveEvent(tripId)

	const [state, setState] = useState<EventEditState | null>(null)
	const [step, setStep] = useState<EventCreateStep>('what')
	const [isSaving, setIsSaving] = useState(false)
	const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false)

	const event = eventsData?.events.find(candidate => candidate.id === eventId)

	// Seed once, from the first event that lands — later refetches (a realtime
	// nudge) must not reset what the user is editing. The files come first: the
	// event's own count toward the attachment cap.
	useEffect(() => {
		if (event && filesData) setState(current => current ?? seedEditState(event, filesData.files))
	}, [event, filesData])

	const loadError = tripError || eventsError || filesError
	if (loadError) return <ErrorNote>Error loading event: {loadError.message}</ErrorNote>
	if (tripLoading || eventsLoading || filesLoading || (event && !state)) return <PageLoader label="Loading event…" />
	if (!trip) return <ErrorNote>Trip not found</ErrorNote>
	if (!state) return <ErrorNote>Event not found</ErrorNote>

	const leave = (dayIndex: number) => navigate(itineraryPath(tripId, dayIndex))

	const handleClose = () => {
		if (hasChanges(state)) setIsConfirmingDiscard(true)
		else leave(state.seeded.tripDayIndex)
	}

	const handleSave = async () => {
		setIsSaving(true)
		const saved = await saveEvent(state.eventId, eventName(state.draft), toUpdatePayload(state), {
			removedFileIds: removedFileIds(state),
			newFiles: state.draft.files,
		})
		setIsSaving(false)
		// A failed save keeps the edit; the save has said why.
		if (saved) leave(state.draft.tripDayIndex)
	}

	return (
		<PageScroll>
			<EventFlow
				title="Edit Event"
				trip={trip}
				dayInfos={dayInfosData?.tripDayInfo}
				draft={state.draft}
				onChange={draft => setState(updateDraft(state, draft))}
				step={step}
				onStepChange={setStep}
				bar={day => editBottomBar(state, step, day)}
				ctaPending={isSaving}
				onSubmit={handleSave}
				onClose={handleClose}
			/>

			<ConfirmationModal
				isOpen={isConfirmingDiscard}
				onClose={() => setIsConfirmingDiscard(false)}
				onConfirm={() => leave(state.seeded.tripDayIndex)}
				title="Discard your changes?"
				message="Your edits to this event won't be saved."
				confirmText="Discard"
			/>
		</PageScroll>
	)
}

export default EventEditPage
